import React, {useEffect, useState} from 'react';
import {Route, Routes} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {fetchModules, setModules} from '../actions/index.js';
import {
    ADMIN_SEM_CREDENCIAL,
    TODOS_MODULOS_SYSBENEF,
    URL_PROGRAMAS,
    URL_TIPOS_HABITACAO,
    URL_TIPOS_MORADIA
} from '../constants/index.js';
import useErrorMessage from '../hooks/useErrorMessage.js';
import PageWrapper from './shared/pagewrapper/PageWrapper.js';
import Message from './shared/message/Message.js';
import OptionBar from './shared/optionbar/OptionBar.js';
import Menu from './administrativo/Menu.jsx';
import PageInicio from './administrativo/pages/PageInicio.jsx';
import PageEmMigracao from './administrativo/pages/PageEmMigracao.jsx';
import PageAcompanhamento from './administrativo/pages/PageAcompanhamento.jsx';
import PageCadastroDescricao from './administrativo/pages/cadastro/PageCadastroDescricao.jsx';
import PageDocumentos from './administrativo/pages/cadastro/PageDocumentos.jsx';
import PageCoordenadores from './administrativo/pages/cadastro/PageCoordenadores.jsx';
import PageResponsaveis from './administrativo/pages/cadastro/PageResponsaveis.jsx';
import PageAtendentes from './administrativo/pages/cadastro/PageAtendentes.jsx';
import PageProcessosSelecao from './administrativo/pages/cadastro/processo/PageProcessosSelecao.jsx';
import PageProcessoSelecao from './administrativo/pages/cadastro/processo/PageProcessoSelecao.jsx';
import PageInscricoes from './administrativo/pages/inscricao/PageInscricoes.jsx';
import PageInscricao from './administrativo/pages/inscricao/PageInscricao.jsx';
import PageAvaliacaoDocumentos from './administrativo/pages/inscricao/PageAvaliacaoDocumentos.jsx';
import PageCargaPbest from './administrativo/pages/cargasaf/PageCargaPbest.jsx';
import PageCargaProuni from './administrativo/pages/cargasaf/PageCargaProuni.jsx';
import PageCargaPesquisa from './administrativo/pages/cargasaf/PageCargaPesquisa.jsx';
import PageEnvioEmails from './administrativo/pages/recursos/PageEnvioEmails.jsx';
import PageRelatorioTermosConcessao from './administrativo/pages/relatorio/PageRelatorioTermosConcessao.jsx';
import PageNaoEncontrada from './PageNaoEncontrada.jsx';

/*
 * Administrativo React, aberto pelo portal em /administrativo?credencial=...
 * Carrega os módulos do usuário (SYSBENEF) antes de montar menu e rotas. As
 * telas do PrimeFaces ainda não migradas abrem o PageEmMigracao: ao migrar
 * uma tela, troca-se só o element da rota dela.
 */
export default function ModuloAdministrativo() {
    const dispatch = useDispatch();
    const showErrorMessage = useErrorMessage();
    const credencial = useSelector(state => state.credencial);
    // 'carregando' | 'carregado' | 'erro'
    const [situacao, setSituacao] = useState('carregando');

    /* Só na entrada: a credencial muda a cada resposta (renovação) e não deve recarregar os módulos. */
    useEffect(() => {
        // Modo de teste: sem credencial e com todos os módulos liberados (ver ADMIN_SEM_CREDENCIAL).
        if (ADMIN_SEM_CREDENCIAL) {
            dispatch(setModules(TODOS_MODULOS_SYSBENEF));
            setSituacao('carregado');
            return;
        }

        if (!credencial) {
            showErrorMessage('Uma credencial é necessária!');
            setSituacao('erro');
            return;
        }

        // Sem os módulos não há menu: se falhar, a mensagem do back já aparece e a aplicação fica não carregada.
        dispatch(fetchModules())
            .then(() => setSituacao('carregado'))
            .catch(() => setSituacao('erro'));
    }, []);

    // Mesmo loader estático do index.html (estilos .carregamento-inicial lá): segue da carga do bundle até o menu,
    // também no modo embedded, em que o LoadingModal global não aparece (o loading iria por postMessage à janela pai).
    if (situacao === 'carregando') {
        return (
            <div className='carregamento-inicial'>
                <div className='carregamento-inicial__caixa'>
                    <div className='carregamento-inicial__spinner' role='progressbar'/>
                    <span>Carregando, aguarde...</span>
                </div>
            </div>
        );
    }

    if (situacao === 'erro') {
        return (
            <PageWrapper>
                <Message type='blank'>Aplicação não carregada.</Message>
            </PageWrapper>
        );
    }

    return (
        <>
            <OptionBar>
                <Menu/>
            </OptionBar>
            <Routes>
                <Route index element={<PageInicio/>}/>
                <Route path='acompanhamento' element={<PageAcompanhamento/>}/>
                <Route path='inscricoes' element={<PageInscricoes/>}/>
                <Route path='inscricoes/novo' element={<PageInscricao/>}/>
                <Route path='inscricoes/:id' element={<PageInscricao/>}/>
                <Route path='inscricoes/:id/documentos' element={<PageAvaliacaoDocumentos/>}/>
                <Route path='cadastros/programas' element={
                    <PageCadastroDescricao key='programas' titulo='Programa' nomeItem='Programa' url={URL_PROGRAMAS} modulo='PROGRAMA'/>
                }/>
                <Route path='cadastros/documentos' element={<PageDocumentos/>}/>
                <Route path='cadastros/tipos-habitacao' element={
                    <PageCadastroDescricao key='tipos-habitacao' titulo='Tipo de Habitação' nomeItem='Tipo de habitação' url={URL_TIPOS_HABITACAO} modulo='HABITACAO'/>
                }/>
                <Route path='cadastros/tipos-moradia' element={
                    <PageCadastroDescricao key='tipos-moradia' titulo='Tipo de Moradia' nomeItem='Tipo de moradia' url={URL_TIPOS_MORADIA} modulo='MORADIA'/>
                }/>
                <Route path='cadastros/processos-selecao' element={<PageProcessosSelecao/>}/>
                <Route path='cadastros/processos-selecao/novo' element={<PageProcessoSelecao/>}/>
                <Route path='cadastros/processos-selecao/:id' element={<PageProcessoSelecao/>}/>
                <Route path='cadastros/coordenadores' element={<PageCoordenadores/>}/>
                <Route path='cadastros/postos-atendimento' element={<PageEmMigracao titulo='Postos de Atendimento' modulo='CFPOSTATEN'/>}/>
                <Route path='cadastros/atendentes' element={<PageAtendentes/>}/>
                <Route path='cadastros/escalas' element={<PageEmMigracao titulo='Escalas' modulo='CFCADESCAT'/>}/>
                <Route path='cadastros/disponibilidades' element={<PageEmMigracao titulo='Disponibilidades' modulo='CFDISPONIB'/>}/>
                <Route path='cadastros/responsaveis' element={<PageResponsaveis/>}/>
                <Route path='carga-saf/pbest' element={<PageCargaPbest/>}/>
                <Route path='carga-saf/prouni-fies' element={<PageCargaProuni/>}/>
                <Route path='carga-saf/pesquisa' element={<PageCargaPesquisa/>}/>
                <Route path='recursos/envio-emails' element={<PageEnvioEmails/>}/>
                <Route path='relatorios/termos-concessao' element={<PageRelatorioTermosConcessao/>}/>
                <Route path='*' element={<PageNaoEncontrada/>}/>
            </Routes>
        </>
    );
}
